import { BehaviorSubject, catchError, from, map, of } from 'rxjs';
import { inject } from '@angular/core';
import { BIMPLUS_TOKEN_FACTORY } from '../../../tokens';
import { AuthStoreService } from '../../../../../../data-access/auth/auth-store.service';
import { isJwtExpired } from 'utilities';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import type { Api } from 'bimplus-websdk';

/** sessionStorage key used to remember the last embedded token validated in this browser tab. */
const EMBEDDED_TOKEN_STORAGE_KEY = 'ocp.embedded.session-token';

export class WithEmbeddedValidationHandlerService {
  private readonly _validationState = new BehaviorSubject<string | null>(null);
  readonly validationState = this._validationState.asObservable();
  private readonly tokenFactory = inject(BIMPLUS_TOKEN_FACTORY);
  private readonly authService = inject(AuthStoreService);
  private readonly bimplusApi = inject<Api | null>(BIMPLUS_API, { optional: true });

  handleMissingToken() {
    this._validationState.next(null);
    return of(null);
  }

  handleToken(token: string) {
    this.tokenFactory.setToken(token);
    this._validationState.next(token);
    this.authService.updateAccessToken(token);
    this.authService.setAuthenticated({
      isAuthenticated: true,
      allConfigsAuthenticated: [],
    });
    sessionStorage.setItem(EMBEDDED_TOKEN_STORAGE_KEY, token);
    void this.populateBimplusUserId(token);
  }

  handleValidateToken(token: string) {
    if (!this.bimplusApi) {
      return of(null);
    }

    return from(this.bimplusApi.user.get(token)).pipe(
      map(user => {
        const userId = user?.id ?? '';
        if (!userId) {
          this.authService.updateBimplusUserId('');
          return null;
        }

        this.authService.updateBimplusUserId(userId);
        return token;
      }),
      catchError(() => {
        this.authService.updateBimplusUserId('');
        return of(null);
      })
    );
  }

  /**
   * OCP-1464: Determines whether the provided token can reuse the existing authenticated
   * session, skipping the re-authentication/validation flow entirely.
   *
   * Requires all of:
   * - an already authenticated session,
   * - the same token that was previously validated in this browser tab,
   * - a token that has not expired.
   */
  hasValidStoredSession(token: string): boolean {
    if (!this.authService.isAuthenticated()) {
      return false;
    }

    const storedToken = sessionStorage.getItem(EMBEDDED_TOKEN_STORAGE_KEY);
    if (!storedToken || storedToken !== token) {
      return false;
    }

    return isJwtExpired(token) === false;
  }

  getValidStoredSessionToken(): string | null {
    if (!this.authService.isAuthenticated()) {
      return null;
    }

    const storedToken = sessionStorage.getItem(EMBEDDED_TOKEN_STORAGE_KEY);
    if (!storedToken) {
      return null;
    }

    return isJwtExpired(storedToken) ? null : storedToken;
  }

  private async populateBimplusUserId(token: string): Promise<void> {
    if (!this.bimplusApi || !token) {
      return;
    }

    try {
      const user = await this.bimplusApi.user.get(token);
      this.authService.updateBimplusUserId(user?.id ?? '');
    } catch {
      this.authService.updateBimplusUserId('');
    }
  }
}
